import { FormControl, InputAdornment, TextField } from '@mui/material'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { connectField } from 'dynamic-uniforms'
import { Markdown } from '../../Markdown'
import { useCiboAutoForm } from './CiboAutoFormContext'
import { ClearButton } from './ClearButton'
import { FieldLabel } from './FieldLabel'
import { filterDOMProps } from './filterDOMProps'
import { ValidationMessages } from './ValidationMessages'

interface ClearableTextFieldProps {
  value: string | undefined | null
  onChange: (value: string | undefined) => void
  label?: string
  disabled?: boolean
  error?: any
  helperText?: string
  required?: boolean
  name: string
  placeholder?: string
  [key: string]: any // uniforms injects many props
}

function ClearableTextField({
  value,
  onChange,
  label,
  error,
  helperText,
  disabled,
  required,
  placeholder,
  description,
  ...props
}: ClearableTextFieldProps) {
  const { touch } = useCiboAutoForm()
  const canClear = !disabled && value != null && value !== ''

  return (
    <FormControl fullWidth {...filterDOMProps(props)} disabled={disabled}>
      {label && <FieldLabel label={label} required={required} />}

      {props.field.format === 'date' ? (
        <MuiDatePicker
          enableAccessibleFieldDOMStructure={false}
          disabled={disabled}
          format="YYYY-MM-DD"
          minDate={props.field.formatMinimum ? dayjs(props.field.formatMinimum) : undefined}
          maxDate={props.field.formatMaximum ? dayjs(props.field.formatMaximum) : undefined}
          onChange={newValue => {
            if (disabled) return
            if (newValue && dayjs(newValue).isValid()) {
              onChange(dayjs(newValue).format('YYYY-MM-DD'))
            } else {
              onChange(undefined)
            }
            touch(props.name)
          }}
          value={value ? dayjs(value) : null}
          slots={{
            textField: params => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      {canClear && (
                        <ClearButton
                          tooltipKey="clearButton.value"
                          onClick={() => {
                            onChange(undefined)
                            touch(props.name)
                          }}
                        />
                      )}
                      {params.InputProps?.endAdornment}
                    </InputAdornment>
                  ),
                }}
              />
            ),
          }}
        />
      ) : (
        <TextField
          data-testid={`text-field-${props.name}`}
          {...filterDOMProps(props)}
          value={value ?? ''}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          helperText={helperText}
          onChange={e => {
            const v = e.target.value
            // Empty string → undefined (Uniforms best practice)
            if (v === '') onChange(undefined)
            else onChange(v)

            touch(props.name)
          }}
          onBlur={() => touch(props.name)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {canClear && (
                    <ClearButton
                      tooltipKey="clearButton.value"
                      onClick={() => onChange(undefined)}
                    />
                  )}
                </InputAdornment>
              ),
            },
          }}
          fullWidth
        />
      )}

      <ValidationMessages fieldName={props.name} error={error} />

      {!!description && <Markdown typographyProps={{ variant: 'caption' }}>{description}</Markdown>}
    </FormControl>
  )
}

export default connectField(ClearableTextField)
